import { useState, useEffect } from "react";
import { Search, Plus, Check, Loader2, User, Mail, Phone, MapPin } from "lucide-react";
import { quoteJobService } from "../../../../services/quoteJobService";
import { ZipAutoFillInput } from "../../components/ZipAutoFillInput";

const titles = ["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "No title"];

export default function ClientSelection({
    selectedClient,
    setSelectedClient,
    newClientData,
    setNewClientData,
    clientMode = "search",
    setClientMode
}) {
    const [searchQuery, setSearchQuery] = useState({ name: "", phone: "", email: "" });
    const [clients, setClients] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    useEffect(() => {
        if (clientMode === "search") {
            handleSearchClients();
        }
    }, [clientMode]);

    const handleSearchClients = async () => {
        setSearchLoading(true);
        try {
            const response = await quoteJobService.searchClients({});
            if (response.data.success) {
                setClients(response.data.data || []);
            }
        } catch (error) {
            console.error("Failed to load clients:", error);
        } finally {
            setSearchLoading(false);
        }
    };

    const filteredClients = clients.filter(client => {
        const nameMatch = !searchQuery.name ||
            (client.first_name + " " + client.last_name).toLowerCase().includes(searchQuery.name.toLowerCase());
        const phoneMatch = !searchQuery.phone ||
            (client.phone && client.phone.includes(searchQuery.phone));
        const emailMatch = !searchQuery.email ||
            (client.email && client.email.toLowerCase().includes(searchQuery.email.toLowerCase()));

        return nameMatch && phoneMatch && emailMatch;
    });

    return (
        <div className="space-y-6">
            <div className="flex gap-4 mb-6">
                <button
                    type="button"
                    onClick={() => setClientMode("search")}
                    className={`
            flex-1 py-3 px-6 rounded-lg font-semibold transition-all text-sm
            ${clientMode === "search"
                            ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"}
          `}
                >
                    <Search className="w-5 h-5 inline mr-2" />
                    Search Client
                </button>
                <button
                    type="button"
                    onClick={() => setClientMode("create")}
                    className={`
            flex-1 py-3 px-6 rounded-lg font-semibold transition-all text-sm
            ${clientMode === "create"
                            ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"}
          `}
                >
                    <Plus className="w-5 h-5 inline mr-2" />
                    Create New Client
                </button>
            </div>

            {clientMode === "search" ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5" />
                                Name
                            </label>
                            <input
                                type="text"
                                placeholder="Search by name"
                                value={searchQuery.name}
                                onChange={(e) => setSearchQuery(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5" />
                                Phone
                            </label>
                            <input
                                type="text"
                                placeholder="Search by phone"
                                value={searchQuery.phone}
                                onChange={(e) => setSearchQuery(prev => ({ ...prev, phone: e.target.value }))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5" />
                                Email
                            </label>
                            <input
                                type="email"
                                placeholder="Search by email"
                                value={searchQuery.email}
                                onChange={(e) => setSearchQuery(prev => ({ ...prev, email: e.target.value }))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                            />
                        </div>
                    </div>

                    {searchLoading && (
                        <div className="flex items-center justify-center py-2">
                            <Loader2 className="w-5 h-5 animate-spin text-blue-500 mr-2" />
                            <span className="text-sm text-gray-600">Searching...</span>
                        </div>
                    )}

                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {filteredClients.length === 0 && !searchLoading ? (
                            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                <User className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                                <p className="text-sm font-medium text-gray-900">No clients found</p>
                                <p className="text-xs text-gray-500 mt-1">Try adjusting your search or create a new client</p>
                            </div>
                        ) : (
                            filteredClients.map(client => (
                                <div
                                    key={client.id}
                                    onClick={() => setSelectedClient(client)}
                                    className={`
                  p-4 border-2 rounded-lg cursor-pointer transition-all
                  ${selectedClient?.id === client.id
                                            ? "border-blue-500 bg-blue-50 shadow-md"
                                            : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}
                `}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <p className="text-sm font-bold text-gray-900">
                                                {client.first_name} {client.last_name}
                                            </p>
                                            <div className="flex items-center gap-3 mt-1.5">
                                                <p className="text-xs text-gray-600 flex items-center gap-1">
                                                    <Mail className="w-3 h-3" />
                                                    {client.email}
                                                </p>
                                                <p className="text-xs text-gray-600 flex items-center gap-1">
                                                    <Phone className="w-3 h-3" />
                                                    {client.phone}
                                                </p>
                                            </div>
                                        </div>
                                        {selectedClient?.id === client.id && (
                                            <div className="ml-3">
                                                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                                                    <Check className="w-5 h-5 text-white" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4">Client Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Title
                                </label>
                                <select
                                    value={newClientData.title}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                >
                                    <option value="">Select title</option>
                                    {titles.map((title) => (
                                        <option key={title} value={title}>
                                            {title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter first name"
                                    value={newClientData.first_name}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, first_name: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter last name"
                                    value={newClientData.last_name}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, last_name: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Company Name
                                </label>
                                <input
                                    type="text"
                                    placeholder="Company (optional)"
                                    value={newClientData.company_name}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, company_name: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4">Contact Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                    <Mail className="w-3.5 h-3.5" />
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    placeholder="email@example.com"
                                    value={newClientData.email}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                    <Phone className="w-3.5 h-3.5" />
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    placeholder="+1 (555) 000-0000"
                                    value={newClientData.phone}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, phone: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    placeholder="Enter password"
                                    value={newClientData.password}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, password: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                                <p className="mt-1 text-xs text-gray-500">Client will use this to login</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" />
                            Address
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Street Address
                                </label>
                                <input
                                    type="text"
                                    placeholder="123 Main St"
                                    value={newClientData.street_1}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, street_1: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    City
                                </label>
                                <input
                                    type="text"
                                    placeholder="City"
                                    value={newClientData.city}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, city: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    State
                                </label>
                                <input
                                    type="text"
                                    placeholder="State"
                                    value={newClientData.state}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, state: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Zip Code
                                </label>
                                <ZipAutoFillInput
                                    zip={newClientData.postal_code}
                                    onZipChange={(zip) => setNewClientData(prev => ({ ...prev, postal_code: zip || "" }))}
                                    onAddressResolved={({ city, state, country }) => {
                                        setNewClientData(prev => ({
                                            ...prev,
                                            city: city || prev.city,
                                            state: state || prev.state,
                                            country: country || prev.country
                                        }));
                                    }}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Country
                                </label>
                                <input
                                    type="text"
                                    placeholder="USA"
                                    value={newClientData.country}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, country: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
